import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { FaChevronLeft, FaChevronRight, FaPlay, FaYoutube, FaSpinner } from 'react-icons/fa';

// ============================================================
// REPLACE THIS WITH YOUR ACTUAL CHANNEL ID
// Find it at: https://www.youtube.com/@estateghar → View Page Source → search "channelId"
// ============================================================
const CHANNEL_ID = 'UCnNbBmTVDdzAKzMGkjAAX4A'; // <-- PUT YOUR CHANNEL ID HERE

// Public proxy to bypass YouTube's RSS blocking
const RSS_PROXY = 'https://api.rss2json.com/v1/api.json?rss_url=';

const YouTubeCarousel = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // ============================================================
  // FETCH VIDEOS FROM YOUR CHANNEL'S RSS FEED
  // ============================================================
  useEffect(() => {
    const fetchVideos = async () => {
      if (!CHANNEL_ID || CHANNEL_ID.includes('xxxx')) {
        setError('Please set your CHANNEL_ID in the component');
        setLoading(false);
        return;
      }

      try {
        const feedUrl = `https://www.youtube.com/feeds/videos.xml?channel_id=${CHANNEL_ID}`;
        const response = await fetch(`${RSS_PROXY}${encodeURIComponent(feedUrl)}`);
        const data = await response.json();

        if (data.status === 'ok' && data.items && data.items.length > 0) {
          const parsedVideos = data.items.map((item) => {
            // Extract video ID from the link
            const videoId = item.link?.split('v=')[1]?.split('&')[0] || '';
            
            return {
              id: videoId,
              title: item.title || 'Untitled Video',
              thumbnail: item.thumbnail || `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`,
              publishedAt: item.pubDate || new Date().toISOString(),
              views: item.viewCount ? `${Number(item.viewCount).toLocaleString()} views` : 'Video',
              link: item.link || `https://www.youtube.com/watch?v=${videoId}`,
            };
          }).filter(v => v.id);

          setVideos(parsedVideos);
        } else {
          setError('No videos found. Check your Channel ID.');
        }
      } catch (err) {
        console.error('Error fetching videos:', err);
        setError('Failed to load videos. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // Check scroll position
  const updateScrollButtons = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollButtons);
      updateScrollButtons();
      return () => el.removeEventListener('scroll', updateScrollButtons);
    }
  }, [videos]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.8;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const openVideo = (video) => {
    setActiveVideo(video);
    document.body.style.overflow = 'hidden';
  };

  const closeVideo = () => {
    setActiveVideo(null);
    document.body.style.overflow = 'unset';
  };

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeVideo();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, []);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  };

  return (
    <div className="relative w-full py-12 bg-gradient-to-br from-amber-50 via-rose-50 to-sky-100 overflow-hidden">
      {/* Decorative blurred orbs */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-amber-300/40 blur-3xl" />
        <div className="absolute top-1/3 -right-24 h-96 w-96 rounded-full bg-rose-300/40 blur-3xl" />
        <div className="absolute bottom-0 left-1/4 h-96 w-96 rounded-full bg-sky-300/40 blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-500/10 backdrop-blur-md border border-red-300/50 text-red-700 rounded-full text-sm font-semibold mb-3 shadow-sm">
              <FaYoutube className="w-4 h-4" />
              EstateGhar Channel
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
              Watch Our Latest Videos
            </h2>
            <p className="text-gray-600 mt-1">
              Property tours, investment tips, and market insights
            </p>
          </div>

          <a
            href="https://www.youtube.com/@estateghar"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl shadow-lg shadow-red-500/25 hover:shadow-red-500/40 transition-all duration-300"
          >
            <FaYoutube className="w-5 h-5" />
            Subscribe
          </a>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <FaSpinner className="w-10 h-10 text-amber-500 animate-spin" />
              <p className="text-gray-600 font-medium">Loading videos...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="text-center py-16 bg-white/60 backdrop-blur-xl border border-white/60 rounded-2xl">
            <FaYoutube className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-gray-700 font-semibold text-lg mb-2">Unable to load videos</p>
            <p className="text-gray-500 text-sm mb-4">{error}</p>
            <a
              href="https://www.youtube.com/@estateghar/videos"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
            >
              <FaYoutube className="w-4 h-4" />
              View on YouTube
            </a>
          </div>
        )}

        {/* Carousel */}
        {!loading && !error && videos.length > 0 && (
          <>
            <div className="relative group">
              {/* Left Arrow */}
              <button
                onClick={() => scroll('left')}
                className={`absolute left-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/80 backdrop-blur-md border border-white/60 shadow-lg text-gray-700 hover:bg-amber-400 hover:text-white transition-all duration-300 ${
                  canScrollLeft ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll left"
              >
                <FaChevronLeft className="w-5 h-5" />
              </button>

              <div
                ref={scrollRef}
                className="flex gap-5 overflow-x-auto scroll-smooth pb-4 px-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                <style>{`.overflow-x-auto::-webkit-scrollbar { display: none; }`}</style>

                {videos.map((video) => (
                  <button
                    key={video.id}
                    onClick={() => openVideo(video)}
                    className="flex-shrink-0 w-[320px] sm:w-[360px] group/card text-left"
                  >
                    <div className="bg-white/60 backdrop-blur-xl border border-white/60 rounded-2xl shadow-[0_8px_32px_0_rgba(31,38,135,0.10)] overflow-hidden hover:shadow-[0_12px_40px_0_rgba(31,38,135,0.18)] transition-all duration-300 hover:-translate-y-1">
                      <div className="relative aspect-video bg-gray-200 overflow-hidden">
                        <img
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full h-full object-cover group-hover/card:scale-105 transition-transform duration-500"
                          loading="lazy"
                          onError={(e) => {
                            e.target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                          }}
                        />
                        <div className="absolute inset-0 bg-black/20 group-hover/card:bg-black/40 transition-colors duration-300 flex items-center justify-center">
                          <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover/card:scale-110 transition-transform duration-300">
                            <FaPlay className="w-5 h-5 text-white ml-1" />
                          </div>
                        </div>
                      </div>

                      <div className="p-4">
                        <h3 className="font-semibold text-gray-900 text-sm leading-snug line-clamp-2 mb-2 group-hover/card:text-amber-700 transition-colors">
                          {video.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{video.views}</span>
                          <span className="w-1 h-1 rounded-full bg-gray-400" />
                          <span>{formatDate(video.publishedAt)}</span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Right Arrow */}
              <button
                onClick={() => scroll('right')}
                className={`absolute right-0 top-1/2 -translate-y-1/2 z-10 p-3 rounded-full bg-white/80 backdrop-blur-md border border-white/60 shadow-lg text-gray-700 hover:bg-amber-400 hover:text-white transition-all duration-300 ${
                  canScrollRight ? 'opacity-100' : 'opacity-0 pointer-events-none'
                }`}
                aria-label="Scroll right"
              >
                <FaChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="text-center mt-8">
              <a
                href="https://www.youtube.com/@estateghar/videos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 font-medium transition-colors"
              >
                View all videos on YouTube
                <FaChevronRight className="w-3 h-3" />
              </a>
            </div>
          </>
        )}
      </div>

      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeVideo}
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold truncate pr-4">
                {activeVideo.title}
              </h3>
              <button
                onClick={closeVideo}
                className="flex-shrink-0 w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-colors"
                aria-label="Close video"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="relative pt-[56.25%] rounded-xl overflow-hidden shadow-2xl">
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubeCarousel;